import React, { useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  Paper, Stack, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import PostedGate from '../operations/PostedGate';
import ScanChallan from '../operations/ScanChallan';
import { useOperations } from '../operations/OperationsProvider';
import { driverLabel } from '../operations/drivers';
import {
  clearance, exitStage, exitIsNext, settled,
} from './clearance';
import { tintedPanelSx } from '../common/theme/accents';

const normalize = (value) => String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

// The barrier on the way out.
//
// One question gets asked here, and it is not about paperwork: has the plant got its portable
// tracker back? A lorry on its own unit drives away and nobody needs to think about it. A lorry
// that borrowed one of ours has to hand it over at the barrier, and if it does not, the unit leaves
// on a lorry that is not coming back.
//
// So the desk is a number and an answer. Type the plate, scan it, or let a barcode or RFID gun type
// it into the field like a keyboard - then red or green, large enough to read from arm's length,
// because the man reading it is standing outside next to a running engine.
//
// Where a unit has to come off, it takes two presses and not one, because the rules will not detach
// the only tracker from a running trip or record a return before a detach - both of which are right.
// The gate out is recorded first, which finishes the trip, and the unit is booked in second. The
// popup stays up across the two: closing it in between is the moment the tracker drives away.
// Whether the lorry is held at all is decided in ./clearance, which also knows that a load going
// out to a customer is meant to take its tracker with it.
const GateOutDesk = ({ trips }) => {
  const { data, mutate } = useOperations();
  const [query, setQuery] = useState('');
  const [scanning, setScanning] = useState(false);
  const [checking, setChecking] = useState(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const camera = typeof document !== 'undefined' && 'capture' in document.createElement('input');
  const key = normalize(query);
  const answering = key.length < 3 ? [] : trips
    .filter((t) => !t.closedAt && [t.vehicle, `Q-${t.id}`, t.challanNumber, t.shipmentId].some((value) => normalize(value).includes(key)));
  const matches = answering.slice(0, 3);

  // Read fresh out of the live data each render, so collecting a tracker turns the popup from red to
  // green in front of the operator rather than leaving him to work out whether it took.
  const trip = checking ? trips.find((t) => t.id === checking) : null;
  const verdict = clearance(trip, data);

  const collect = async (assignmentId) => {
    setPending(true); setError(null);
    try {
      await mutate(`assignments/${assignmentId}/return`, {});
    } catch (err) { setError(err.message); } finally { setPending(false); }
  };

  const letGo = async () => {
    setPending(true); setError(null);
    try {
      const stage = exitStage(trip);
      await mutate(`trips/${trip.id}/milestones`, { checkpointId: stage.id });
      setDone(`${trip.vehicle} recorded out at ${stage.name}.`);
      // Judged on what was true when the barrier was reached, which is the right question: if a
      // unit had to come off this lorry, the popup stays up and becomes the step that books it in.
      // Closing here is exactly the moment a tracker drives away.
      if (settled(verdict.state)) { setChecking(null); setQuery(''); }
    } catch (err) { setError(err.message); } finally { setPending(false); }
  };

  // Red for anything that has to happen before or after the barrier; green for a lorry that owes
  // the plant nothing, including one taking a tracker to a customer on purpose.
  const red = !settled(verdict.state) && verdict.state !== 'unknown';
  // Already out: there is nothing to record a second time, and recording it again would raise a
  // sequence exception against a lorry that did nothing wrong.
  const outAlready = !!trip?.gateOut;
  return (
    <Paper variant="outlined" sx={[{ p: 2.5, mb: 3 }, tintedPanelSx('amber')]}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }} justifyContent="space-between">
        <Typography variant="h6">Plant exit</Typography>
        <PostedGate />
      </Stack>
      {done && <Alert severity="success" sx={{ mt: 1.5 }} onClose={() => setDone(null)}>{done}</Alert>}
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TextField
            fullWidth
            autoFocus
            id="gate-out-lookup"
            label="Vehicle number"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              inputProps: { maxLength: 60 },
              endAdornment: query ? (
                <IconButton size="small" aria-label="Clear" onClick={() => setQuery('')}><CloseIcon fontSize="small" /></IconButton>
              ) : null,
            }}
          />
        </Box>
        <Button variant="contained" size="large" startIcon={<PhotoCameraIcon />} sx={{ whiteSpace: 'nowrap', height: 56 }} onClick={() => setScanning(true)}>Scan</Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.75 }}>
        {`${camera ? 'Camera ready — scan the plate or the challan.' : 'No camera on this browser — type the number.'} A barcode or RFID scanner types straight into this field.`}
      </Typography>
      <ScanChallan
        open={scanning}
        onClose={() => setScanning(false)}
        onScanned={({ challanNumber }) => { if (challanNumber) setQuery(challanNumber); }}
      />
      {key.length >= 3 && !answering.length && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No open trip answers to that. A lorry with no open trip has nothing to record a gate out
          against — check the number, or look it up on the trips list.
        </Alert>
      )}
      {answering.length > matches.length && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 2 }}>
          {`${answering.length} open trips answer to that; the first ${matches.length} are below. Add a character or two to narrow it.`}
        </Typography>
      )}
      {matches.map((row) => (
        <Stack key={row.id} direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mt: 2 }}>
          <Typography fontWeight={700}>{row.vehicle}</Typography>
          <Typography variant="body2" color="text.secondary">
            {`Q-${row.id} · ${row.challanNumber || row.shipmentId}${driverLabel(data.drivers, row) ? ` · ${driverLabel(data.drivers, row)}` : ''}`}
          </Typography>
          {!exitStage(row) && <Chip size="small" color="warning" variant="outlined" label="No gate out on this route" />}
          <Button
            sx={{ ml: 'auto' }}
            variant="contained"
            disabled={!exitStage(row)}
            onClick={() => { setChecking(row.id); setError(null); setDone(null); }}
          >
            Check and let out
          </Button>
        </Stack>
      ))}

      {/* Red or green, and nothing else competing with it. */}
      <Dialog open={!!trip} onClose={() => setChecking(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: red ? 'error.main' : 'success.main' }}>
          {red ? <ReportProblemIcon fontSize="large" /> : <CheckCircleIcon fontSize="large" />}
          <Box component="span" sx={{ fontWeight: 700 }}>{verdict.headline}</Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity={red ? 'error' : 'success'} icon={false} sx={{ mb: 2, fontSize: '1rem' }}>
            <Typography fontWeight={700} variant="h6" sx={{ mb: 0.5 }}>{trip?.vehicle}</Typography>
            {verdict.detail}
          </Alert>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {verdict.devices.map((device) => (
            <Stack key={device.assignmentId} direction="row" gap={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
              <Typography variant="body2" fontWeight={600}>{device.identifier}</Typography>
              {device.stillIssued && <Chip size="small" variant="outlined" label="still on the trip" />}
              <Button
                sx={{ ml: 'auto' }}
                variant={device.collectable ? 'contained' : 'outlined'}
                disabled={pending || !device.collectable}
                onClick={() => collect(device.assignmentId)}
              >
                {pending ? 'Recording…' : device.collectable ? 'Collected' : 'After the gate out'}
              </Button>
            </Stack>
          ))}
          {/* The sequence is the route's business, not this barrier's, but a desk about to record a
              stage out of order should be told so rather than find out from an exception later. */}
          {trip && !red && !exitIsNext(trip) && (
            <Alert severity="warning">
              {`The route expects ${(trip.checkpoints[trip.progress] || {}).name || 'another stage'} before the gate out. Recording it here raises an exception.`}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChecking(null)} disabled={pending}>Close</Button>
          {verdict.state !== 'collect' && (
            <Button variant="contained" color={red ? 'warning' : 'success'} disabled={pending || !exitStage(trip) || outAlready} onClick={letGo}>
              {pending ? 'Recording…' : 'Record gate out'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default GateOutDesk;
